import { getBundleComponents } from '@/lib/get-bundle-components';
import { formatPriceEur, formatPriceSek, getB2bPricing } from '@/utils/format-price';

export default class BundleComponents extends HTMLElement {
  constructor() {
    super();
    this.render();
  }

  async render() {
    const productHandle = this.getAttribute('data-product-handle');
    const variantId = this.getAttribute('data-variant-id');
    const componentLabel = this.getAttribute('data-component-label') || 'Package contains';
    const wrapperClass = this.getAttribute('data-class') || '';
    const exitRender = () => {
      if (this.parentElement) {
        this.parentElement.remove();
      } else {
        this.remove();
      }
    };
    if (productHandle && variantId) {
      const variantComponents = await getBundleComponents(productHandle);
      // If no components are found, hide the element and its parent
      if (variantComponents.length === 0) {
        exitRender();
        return;
      }

      // Find the variant component that matches the variant ID
      const variantComponent = variantComponents.find((variant) => variant.id.split('/').pop() === variantId);

      // If no variant component is found, hide the element and its parent
      if (!variantComponent) {
        exitRender();
        return;
      }

      // Get the components for the variant component
      const components = variantComponent?.components.nodes || [];

      // If components is null or empty, hide the element and its parent
      if (!components || components.length === 0) {
        exitRender();
        return;
      }

      this.innerHTML = `
        <div class="flex flex-col m:gap-4x gap-6x ${wrapperClass}">
          <div class="flex items-center gap-x-4x">
            <div class="m:hidden bg-neutral-200 w-full h-[1px] flex-1"></div>
            <div class="flex-none">
              <span class="body-m">${componentLabel}</span>
              <span class="m:hidden">:</span>
            </div>
            <div class="bg-neutral-200 w-full h-[1px] flex-1"></div>
          </div>
          <div class="border-s border-tertiary rounded-m flex flex-col justify-center items-start gap-3x self-stretch p-4x">
            ${components
              .sort((a, b) => Number(b.productVariant.price.amount) - Number(a.productVariant.price.amount))
              .map((component, index) => {
                const baseImageUrl =
                  component.productVariant.image?.url || component.productVariant.product.featuredImage?.url;
                const imageUrl = baseImageUrl
                  ? `${baseImageUrl}${baseImageUrl.includes('?') ? '&' : '?'}image=128`
                  : null;
                const title = component.productVariant.product.title?.trim() ?? '';
                const escapedTitle = this.escapeHtml(title);
                const escapedVariantTitle = this.escapeHtml(component.productVariant.title);
                const variantId = this.getProductVariantFromGid(component.productVariant.id);

                const priceCents = Math.round(Number(component.productVariant.price.amount) * 100);
                const { country } = getB2bPricing();
                const productVariantPrice = country === 'SE' ? formatPriceSek(priceCents) : formatPriceEur(priceCents);

                return `
             <a href="/products/${component.productVariant.product.handle}?variant=${variantId}" target="_blank" rel="noopener noreferrer" class="block w-full hover:bg-gray-50 transition-colors duration-200 rounded-s">
               <div class="flex justify-between items-center gap-4x self-stretch p-2x -m-2x${index < components.length - 1 ? ' border-b-s border-b-tertiary' : ''}">
                <div class="flex items-center gap-4x flex-1">
                  <div class="size-10x rounded-s overflow-hidden bg-gray-200">
                    ${
                      imageUrl
                        ? `<img src="${imageUrl}"
                         alt="${escapedTitle}"
                         class="w-full h-full object-cover"
                         width="56"
                         height="56"
                         loading="lazy" />`
                        : `<div class="w-full h-full flex items-center justify-center">
                             <svg class="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                               <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"></path>
                             </svg>
                           </div>`
                    }
                  </div>
                  <div class="flex flex-col gap-1x flex-1">
                    <div class="flex flex-row justify-between items-center gap-1x self-stretch">
                      <h3 class="text-primary label-m font-medium flex-1 line-clamp-1">${escapedTitle}</h3>
                      <div class="text-primary label-m font-medium">${productVariantPrice}</div>
                    </div>
                    <div class="flex flex-row justify-between items-center gap-1x self-stretch">
                        ${component.productVariant.title !== 'Default Title' ? `<p class="body-s font-light text-secondary">${escapedVariantTitle}</p>` : '<div></div>'}
                        <p class="body-s font-light text-secondary">x${component.quantity}</p>
                      </div>
                  </div>
                </div>

                <span class="hidden" id="bundle-item-${index}">${component.quantity} X ${escapedTitle}</span>
              </div>
            </a>
          `;
              })
              .join('')}
          </div>
        </div>
      `;
    }
  }

  escapeHtml = (text: string): string => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  getProductVariantFromGid = (gid: string): string => {
    const imageUri = gid.split('/');
    if (imageUri?.[0] === 'gid:') {
      return imageUri[imageUri.length - 1];
    }
    return '';
  };
}

if (!customElements.get('bundle-components')) {
  customElements.define('bundle-components', BundleComponents);
}
